import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import { fetchIamStack } from "../../../../store/remediation/remediationSlice";
import { friendlyErrorMessage } from "../common/ErrorCodeMap";
import { useRemediationSnackbar } from "../common/SnackbarHost";

const DEFAULT_ROLE_NAME = "cc-cloudcatcher-role";

const normalizeRoleName = (account) => {
  const role = account?.iamrole || account?.roleName || account?.role_name;
  return (role && String(role).trim()) || DEFAULT_ROLE_NAME;
};

/**
 * Tenant IAM Stack tab.
 *
 * Account list comes from the shared org/accounts store (same `getOrgsAndAccts`
 * response as Configuration → Cloud Accounts). No duplicate accounts API here.
 */
const IamStackPanel = () => {
  const dispatch = useDispatch();
  const { showSnackbar } = useRemediationSnackbar();
  const iamStack = useSelector((s) => s.remediation.iamStack);
  // Same `getOrgsAndAccts` payload as Configuration → Cloud Accounts (orgAccount + acctList).
  const orgAccounts = useSelector((s) => s.orgAccount?.accounts);
  const acctListAccounts = useSelector((s) => s.acctList?.accounts);
  const accountsLoading = useSelector(
    (s) => Boolean(s.orgAccount?.loading || s.acctList?.loading)
  );

  const accounts = useMemo(() => {
    const source =
      orgAccounts?.length > 0 ? orgAccounts : acctListAccounts || [];
    return source.filter(
      (a) => a?.accountNumber && !String(a.accountNumber).includes("INIT")
    );
  }, [orgAccounts, acctListAccounts]);

  const [accountNumber, setAccountNumber] = useState("");
  const [roleName, setRoleName] = useState(DEFAULT_ROLE_NAME);

  const selectedAccount = useMemo(
    () => accounts.find((a) => a.accountNumber === accountNumber) || null,
    [accounts, accountNumber]
  );

  useEffect(() => {
    if (accounts.length === 0 || accountNumber) return;
    setAccountNumber(accounts[0].accountNumber);
  }, [accounts, accountNumber]);

  const handleAccountChange = (e) => {
    const next = e.target.value;
    setAccountNumber(next);
    const acct = accounts.find((a) => a.accountNumber === next);
    setRoleName(acct ? normalizeRoleName(acct) : DEFAULT_ROLE_NAME);
  };

  useEffect(() => {
    if (!accountNumber) return;
    const acct = accounts.find((a) => a.accountNumber === accountNumber);
    const role = normalizeRoleName(acct);
    setRoleName(role);
    if (role.trim()) {
      dispatch(fetchIamStack(role.trim()));
    }
  }, [accountNumber, accounts, dispatch]);

  const handleRegenerate = () => {
    if (!accountNumber) {
      showSnackbar({ message: "Select an AWS account.", severity: "error" });
      return;
    }
    if (!roleName.trim()) {
      showSnackbar({ message: "Role name can't be empty.", severity: "error" });
      return;
    }
    dispatch(fetchIamStack(roleName.trim()));
  };

  const handleCopy = async () => {
    if (!iamStack.template) return;
    try {
      await navigator.clipboard.writeText(iamStack.template);
      showSnackbar({ message: "Template copied to clipboard.", severity: "success" });
    } catch (_e) {
      showSnackbar({ message: "Couldn't copy — please select and copy manually.", severity: "error" });
    }
  };

  const handleDownload = () => {
    if (!iamStack.template) return;
    const blob = new Blob([iamStack.template], { type: "text/yaml;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download =
      iamStack.filename || `${iamStack.role_name || "cc-remediation"}.yaml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
    showSnackbar({ message: "Template downloaded.", severity: "success" });
  };

  return (
    <Box sx={{ pb: 4, mb: 2 }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle sx={{ mb: 0.5 }}>How to use this</AlertTitle>
        <Box component="ol" sx={{ pl: 2, m: 0, fontSize: 13 }}>
          <li>Select the AWS account you are deploying into.</li>
          <li>
            Confirm the IAM role name (pre-filled from Configuration → Cloud Accounts).
          </li>
          <li>Copy the template or download the YAML below.</li>
          <li>
            In the AWS console, open <strong>CloudFormation → Create stack → With new resources</strong>{" "}
            and paste the template into <em>Template body</em>.
          </li>
        </Box>
      </Alert>

      {accountsLoading && accounts.length === 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading accounts…
          </Typography>
        </Box>
      )}

      {!accountsLoading && accounts.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Account list is not loaded yet. Open{" "}
          <strong>Configuration → Cloud Accounts</strong> first (or refresh this page after
          accounts load elsewhere), then return here.
        </Alert>
      )}

      <Stack spacing={0.75} sx={{ mb: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          flexWrap="wrap"
        >
          <FormControl size="small" sx={{ minWidth: 280 }} disabled={accounts.length === 0}>
            <InputLabel id="iam-stack-account-label">AWS account</InputLabel>
            <Select
              labelId="iam-stack-account-label"
              label="AWS account"
              value={accountNumber}
              onChange={handleAccountChange}
            >
              {accounts.map((a) => (
                <MenuItem key={a.accountNumber} value={a.accountNumber}>
                  {a.accountName
                    ? `${a.accountName} (${a.accountNumber})`
                    : a.accountNumber}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="IAM role name"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            sx={{ width: { xs: "100%", sm: 280 } }}
            disabled={!accountNumber}
          />

          <Button
            onClick={handleRegenerate}
            startIcon={<RefreshIcon />}
            variant="outlined"
            size="small"
            disabled={!accountNumber || accounts.length === 0}
            sx={{ flexShrink: 0, alignSelf: { xs: "flex-start", sm: "center" } }}
          >
            Generate template
          </Button>

          <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />

          <Stack
            direction="row"
            spacing={1}
            sx={{ flexShrink: 0, alignSelf: { xs: "flex-start", sm: "center" } }}
          >
            <Button
              onClick={handleCopy}
              startIcon={<ContentCopyIcon />}
              variant="outlined"
              size="small"
              disabled={iamStack.status !== "succeeded"}
            >
              Copy
            </Button>
            <Button
              onClick={handleDownload}
              startIcon={<DownloadIcon />}
              variant="contained"
              size="small"
              disabled={iamStack.status !== "succeeded"}
            >
              Download YAML
            </Button>
          </Stack>
        </Stack>
      </Stack>

      {iamStack.status === "failed" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {friendlyErrorMessage(iamStack.error)}
        </Alert>
      )}

      {iamStack.status === "loading" ? (
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1, mb: 2 }} />
      ) : (
        <Box sx={{ position: "relative", mb: 2 }}>
          <TextField
            multiline
            fullWidth
            minRows={18}
            maxRows={28}
            value={iamStack.template || ""}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: "monospace", fontSize: 12.5, backgroundColor: "grey.50" },
            }}
          />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, mb: 1 }}>
        The template is generated with the role name substituted for your organization.
        Re-generate after you change the role name in Configuration.
      </Typography>
    </Box>
  );
};

export default IamStackPanel;
